/**
 * Regional date anchoring.
 *
 * The server-side anchor inputs for `invariants.md#ssr-safe-rendered-dates`:
 * rendered dates derive from a server-provided anchor + an explicit IANA
 * `timeZone`, never the ambient clock — so SSR and client hydration agree.
 */

/** Default display timezone where no depart city is known (majority Eastern user base). */
export const DEFAULT_DISPLAY_TIMEZONE = 'America/New_York'

function formatYmd(timeZone: string): string {
    // `en-CA` renders `YYYY-MM-DD`; the explicit `timeZone` pins the calendar
    // date so the projection is deterministic regardless of the runtime zone.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date())
}

/**
 * The current calendar date (`YYYY-MM-DD`) in the given IANA `timeZone`.
 * Degrades to {@link DEFAULT_DISPLAY_TIMEZONE} on an unrecognized id
 * (`Intl.DateTimeFormat` throws `RangeError`) rather than crashing the render.
 */
export function todayInTimeZone(timeZone: string): string {
    try {
        return formatYmd(timeZone)
    } catch {
        return formatYmd(DEFAULT_DISPLAY_TIMEZONE)
    }
}

/**
 * A server-computed date anchor threaded to a client search widget so its
 * rendered depart-date is a pure function of the server, never the ambient
 * clock (invariants.md#ssr-safe-rendered-dates). The four anchoring rules:
 *
 * 1. The server renders `today` in Eastern by default (majority user base),
 *    never UTC — {@link defaultDateAnchor}.
 * 2. The client's first render reuses `today` verbatim, so hydration agrees.
 * 3. When an origin zone is known it is carried in {@link originTimeZone}; the
 *    client re-anchors to that same zone after hydration (origin wins).
 * 4. When no origin is known (`originTimeZone` absent), the client re-anchors
 *    to the user's own local day after hydration (the user's zone wins).
 */
export interface DisplayDateAnchor {
    /**
     * "Today" (`YYYY-MM-DD`) as the server computed it — the exact string the
     * initial SSR render and the first client render both use.
     */
    today: string
    /**
     * The authoritative origin zone `today` was computed in, when one is known
     * (e.g. a C2C depart city). Absent → `today` is a display default and the
     * client lets the user's own zone win after hydration.
     */
    originTimeZone?: string
}

/** Anchor for a surface with a known origin zone (e.g. a C2C depart city) — rule 3. */
export function originDateAnchor(originTimeZone: string): DisplayDateAnchor {
    return { today: todayInTimeZone(originTimeZone), originTimeZone }
}

/**
 * Anchor for a surface with no origin (e.g. the homepage): renders the Eastern
 * default on the server so SSR is deterministic (rule 1), carrying no origin so
 * the client lets the user's own zone win after hydration (rule 4).
 */
export function defaultDateAnchor(): DisplayDateAnchor {
    return { today: todayInTimeZone(DEFAULT_DISPLAY_TIMEZONE) }
}
